/**
 * Extracts elements from the main section that are relevant for PDF generation.
 *
 * This function selects specific elements based on predefined CSS selectors
 * and retrieves their dimensions to assist in page layout calculations.
 *
 * @param {HTMLElement} mainSectionNode - The root element of the main section from which elements should be extracted.
 * @returns {Array<{ element: HTMLElement, height: number, useOuterHTML: boolean }>}
 *          An array of objects, each containing:
 *          - `element`: The selected DOM element.
 *          - `height`: The computed height of the element.
 *          - `useOuterHTML`: A flag indicating whether the element's outerHTML should be used instead of innerHTML.
 */
const findMainSectionElements = (mainSectionNode) => {
  let elementsForCapture = [];
  const selectorsArray = [
    "#one-page-report-component .info.v2 .header",
    ".pdf-header-separator",
    ".candidate-details-section",
    ".overall-verdict-container",
    ".cutoff-traits-table",
    ".behavioural-analysis",
    ".fit-score-grid",
    ".fit-score",
    ".single-bucket",
    ".group-container",
    ".p-component .p-traits .p-trait-group",
  ];

  selectorsArray.forEach((selectorClass) => {
    let selectorElements = [];
    selectorElements =
      mainSectionNode?.querySelectorAll(`${selectorClass}`) ?? [];

    selectorElements.forEach((el) => {
      const useOuterHTML =
        selectorClass === "#one-page-report-component .info.v2 .header";
      const rect = el.getBoundingClientRect(); // Get height directly
      elementsForCapture.push({
        element: el,
        height: rect.height,
        useOuterHTML,
      });
    });
  });

  return elementsForCapture;
};

/**
 * Extracts elements from the chat and quiz sections within a given bucket section.
 *
 * This function identifies specific elements such as chat sections, quiz sections,
 * headers, content items, and program-related elements, capturing their dimensions
 * for layout calculations in a PDF report.
 *
 * @param {HTMLElement} bucketSectionNode - The root element containing chat and quiz sections.
 * @returns {Array<{ 
*   element: HTMLElement, 
*   height: number, 
*   startWithNewPage?: boolean, 
*   isChatBucketContainer?: boolean 
* }>} 
* An array of objects representing elements to be captured, where each object contains:
*   - `element`: The selected DOM element.
*   - `height`: The computed height of the element.
*   - `startWithNewPage` (optional): Whether this element should start on a new page in the PDF.
*   - `isChatBucketContainer` (optional): Whether this element is a chat bucket container.
*/
const findChatSectionAndQuizSectionElements = (bucketSectionNode) => {
	let elementsForCapture = [];
	
	const bucketChatSections = Array.from(
		bucketSectionNode.querySelectorAll(".chat-section, .multi-chat-section")
	);
	const bucketQuestionsSections = Array.from(
		bucketSectionNode.querySelectorAll(".questions-section")
	);

	const bucketDetailHeader = bucketSectionNode.querySelector(".bucket-detail-header")

	elementsForCapture.push({ 
		element: bucketDetailHeader, 
		height: bucketDetailHeader.getBoundingClientRect().height,
		startWithNewPage: true,
	});

	if (bucketChatSections.length) {
		const chatDetailElements = Array.from(
			bucketSectionNode.querySelectorAll(".chat-detail-modal.report-pdf")
		);
		const multiChatDetailElements = Array.from(
			bucketSectionNode.querySelectorAll(".multichat-detail-modal.report-pdf")
		);

		bucketChatSections?.forEach((element) => {
			elementsForCapture.push({ 
				element: element, 
				height: element.getBoundingClientRect().height,
				isChatBucketContainer: true,
			});
		})

		chatDetailElements?.forEach((element) => {
			elementsForCapture.push({
				element,
				height: element.getBoundingClientRect().height,
			});
		});

		multiChatDetailElements?.forEach((element) => {
			elementsForCapture.push({
				element,
				height: element.getBoundingClientRect().height,
			});
		});

	} 
	if (bucketQuestionsSections.length) {

		bucketQuestionsSections?.forEach((section) => {
			const sectionHeader = section.querySelector(".section-header")

			elementsForCapture.push({
				element: sectionHeader,
				height: sectionHeader.getBoundingClientRect().height,
			});

			const sectionContent = Array.from(
				section.querySelectorAll(".section-content-item-container")
			);

			sectionContent?.forEach((sectionItem) => {
				elementsForCapture.push({
					element: sectionItem,
					height: sectionItem.getBoundingClientRect().height,
				});
			})
		})
	}
	return elementsForCapture;
}

export {
	findMainSectionElements,
	findChatSectionAndQuizSectionElements
};
